import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useMemo, useState } from "react";
import { useQueries, useQueryClient } from "react-query";
import {
  AlertTriangle,
  ChevronDown,
  ChevronRight,
  Loader2,
  Play,
  X,
} from "lucide-react";
import { getAllCompanyFiles } from "../../api/onboarding";
import {
  BatchEnqueueResult,
  CaptableBatchItem,
  FinancialBatchItem,
  MAX_BATCH_CINS,
  enqueueCaptableBatch,
  enqueueFinancialBatch,
} from "../../api/dataPipeline";
import { useCaptableStore, useFinancialStore } from "../../store";
import { FileTree } from "../FileExplorer/FileExplorer";
import { CompanyAutoSelection, computeAutoSelection } from "./fileSelection";

export interface BatchCompany {
  cin: string;
  name: string | null;
}

type Props = {
  companies: BatchCompany[];
  onClose: () => void;
};

type BatchRow = {
  cin: string;
  name: string | null;
  loading: boolean;
  loadError: string | null;
  selection: CompanyAutoSelection | null;
  captableReady: boolean;
  financialReady: boolean;
};

const STATUS_CLS: Record<string, string> = {
  COMPLETED: "bg-green-100 text-green-700",
  FAILED: "bg-red-100 text-red-700",
  RUNNING: "bg-blue-100 text-blue-700",
  QUEUED: "bg-gray-100 text-gray-500",
};

function baseName(path: string): string {
  return path.split("/").pop() ?? path;
}

// Live result cell for one company: enqueue failure, or the polled run status.
function ResultCell({
  result,
  liveStatus,
}: {
  result: BatchEnqueueResult | undefined;
  liveStatus: string | null;
}) {
  if (!result) return <span className="text-gray-300">—</span>;
  if (!result.ok) {
    return (
      <span
        className="text-xs text-red-600 truncate max-w-[10rem] inline-block"
        title={result.error}
      >
        {result.error ?? "Failed to enqueue"}
      </span>
    );
  }
  const status = liveStatus ?? result.status ?? "QUEUED";
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`text-xs px-1.5 py-0.5 rounded font-medium ${
          STATUS_CLS[status] ?? "bg-gray-100 text-gray-500"
        }`}
      >
        {status}
      </span>
      {result.deduped && (
        <span
          className="text-xs text-amber-600"
          title="An identical run was already queued/running — reusing it."
        >
          dup
        </span>
      )}
    </span>
  );
}

export const BatchExtractModal = ({ companies, onClose }: Props) => {
  // Hard cap per batch request; extra selected rows are left out with a notice.
  const batchCompanies = useMemo(
    () => companies.slice(0, MAX_BATCH_CINS),
    [companies],
  );
  const overflow = companies.length - batchCompanies.length;

  // Same queryKey as the single-CIN popup so trees are shared via the cache.
  const fileQueries = useQueries(
    batchCompanies.map((c) => ({
      queryKey: ["getAllCompanyFiles", c.cin],
      queryFn: getAllCompanyFiles,
      retry: false,
      staleTime: 5 * 60 * 1000,
    })),
  );

  const rows = useMemo<BatchRow[]>(
    () =>
      batchCompanies.map((c, i) => {
        const q = fileQueries[i];
        const selection = q?.data
          ? computeAutoSelection(q.data as FileTree)
          : null;
        return {
          cin: c.cin,
          name: c.name,
          loading: !!q?.isLoading,
          loadError: q?.isError ? "Failed to load files" : null,
          selection,
          captableReady: !!selection?.mgt7 && (selection?.pas3.length ?? 0) > 0,
          financialReady: (selection?.aoc4.length ?? 0) > 0,
        };
      }),
    // useQueries returns a new array each render; depend on the per-query
    // data/state snapshots instead so rows only recompute on real changes.
    [
      batchCompanies,
      ...fileQueries.map((q) => q.data),
      ...fileQueries.map((q) => q.status),
    ],
  );

  const [excluded, setExcluded] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [submitting, setSubmitting] = useState<"captable" | "financial" | null>(
    null,
  );
  const [captableResults, setCaptableResults] = useState<
    Record<string, BatchEnqueueResult>
  >({});
  const [financialResults, setFinancialResults] = useState<
    Record<string, BatchEnqueueResult>
  >({});
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const trackCaptableRun = useCaptableStore((s) => s.trackRun);
  const trackFinancialRun = useFinancialStore((s) => s.trackRun);
  const captableRuns = useCaptableStore((s) => s.runs);
  const financialRuns = useFinancialStore((s) => s.runs);
  const queryClient = useQueryClient();

  const toggleExcluded = (cin: string) => {
    setExcluded((prev) => {
      const next = new Set(prev);
      if (next.has(cin)) next.delete(cin);
      else next.add(cin);
      return next;
    });
  };

  const toggleExpanded = (cin: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(cin)) next.delete(cin);
      else next.add(cin);
      return next;
    });
  };

  const included = (r: BatchRow) => !excluded.has(r.cin);
  // Successfully enqueued companies are not re-submitted; failed ones can retry.
  const captablePending = rows.filter(
    (r) => included(r) && r.captableReady && !captableResults[r.cin]?.ok,
  );
  const financialPending = rows.filter(
    (r) => included(r) && r.financialReady && !financialResults[r.cin]?.ok,
  );
  const anyLoading = rows.some((r) => r.loading);

  const allIncluded = rows.length > 0 && rows.every(included);
  const toggleAllIncluded = () => {
    setExcluded(allIncluded ? new Set(rows.map((r) => r.cin)) : new Set());
  };

  const handleExtractCaptables = async () => {
    if (!captablePending.length || submitting) return;
    setSubmitError(null);
    setNotice(null);
    const items: CaptableBatchItem[] = captablePending.map((r) => {
      const sel = r.selection as CompanyAutoSelection;
      const mgt7 = sel.mgt7 as NonNullable<CompanyAutoSelection["mgt7"]>;
      return {
        cin: r.cin,
        // Tree paths are relative to the company folder; S3 keys are CIN-prefixed.
        mgt7: { key: `${r.cin}/${mgt7.path}`, date: mgt7.date },
        pas3: sel.pas3.map((p) => ({
          key: `${r.cin}/${p.path}`,
          date: p.date,
        })),
      };
    });
    setSubmitting("captable");
    try {
      const data = await enqueueCaptableBatch({ items });
      const map: Record<string, BatchEnqueueResult> = {};
      data.results.forEach((res) => {
        map[res.cin] = res;
        if (res.ok && res.runId && res.jobId) {
          trackCaptableRun({
            runId: res.runId,
            cin: res.cin,
            jobId: res.jobId,
            status: res.status ?? "QUEUED",
          });
        }
      });
      setCaptableResults((prev) => ({ ...prev, ...map }));
      setNotice(`Queued ${data.queued}/${data.total} captable extraction(s).`);
      queryClient.invalidateQueries("listS3Files");
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to enqueue captable batch.",
      );
    } finally {
      setSubmitting(null);
    }
  };

  const handleExtractFinancials = async () => {
    if (!financialPending.length || submitting) return;
    setSubmitError(null);
    setNotice(null);
    const items: FinancialBatchItem[] = financialPending.map((r) => ({
      cin: r.cin,
      aoc4Keys: (r.selection as CompanyAutoSelection).aoc4.map(
        (f) => `${r.cin}/${f.path}`,
      ),
    }));
    setSubmitting("financial");
    try {
      const data = await enqueueFinancialBatch({ items });
      const map: Record<string, BatchEnqueueResult> = {};
      data.results.forEach((res) => {
        map[res.cin] = res;
        if (res.ok && res.runId && res.jobId) {
          trackFinancialRun({
            runId: res.runId,
            cin: res.cin,
            jobId: res.jobId,
            status: res.status ?? "QUEUED",
          });
        }
      });
      setFinancialResults((prev) => ({ ...prev, ...map }));
      setNotice(`Queued ${data.queued}/${data.total} financial extraction(s).`);
      queryClient.invalidateQueries("listS3Files");
    } catch (err: any) {
      setSubmitError(
        err?.response?.data?.message ??
          err?.message ??
          "Failed to enqueue financial batch.",
      );
    } finally {
      setSubmitting(null);
    }
  };

  const liveStatus = (
    result: BatchEnqueueResult | undefined,
    runs: Record<string, { status: string }>,
  ): string | null => {
    if (!result?.ok || !result.runId) return null;
    return runs[result.runId]?.status ?? null;
  };

  const handleClose = () => {
    // Refresh the listing so the grid reflects any runs queued from here.
    if (
      Object.keys(captableResults).length ||
      Object.keys(financialResults).length
    ) {
      queryClient.invalidateQueries("listS3Files");
    }
    onClose();
  };

  return (
    <Transition.Root show as={Fragment}>
      <Dialog as="div" className="relative z-20" onClose={handleClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-60 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 z-10 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-5xl rounded-lg bg-white shadow-xl flex flex-col max-h-[88vh]">
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
                <Dialog.Title className="text-sm font-semibold text-gray-900">
                  Batch Extract — {batchCompanies.length} compan
                  {batchCompanies.length === 1 ? "y" : "ies"}
                </Dialog.Title>
                <button
                  type="button"
                  onClick={handleClose}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="px-4 py-2 space-y-1 flex-shrink-0">
                <p className="text-xs text-gray-500">
                  For each company the latest MGT-7 plus all PAS-3 filings are
                  auto-picked for captable extraction, and all AOC-4 filings for
                  financial extraction — duplicate same-date copies are skipped
                  (highest sequence number wins).
                </p>
                {overflow > 0 && (
                  <p className="text-xs text-amber-700 bg-amber-50 rounded px-2 py-1 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Max {MAX_BATCH_CINS} companies per batch — the last{" "}
                    {overflow} selected compan{overflow === 1 ? "y" : "ies"}{" "}
                    {overflow === 1 ? "was" : "were"} left out.
                  </p>
                )}
                {notice && <p className="text-xs text-green-700">{notice}</p>}
                {submitError && (
                  <p className="text-xs text-red-600">{submitError}</p>
                )}
              </div>

              <div className="overflow-y-auto flex-1 px-4 pb-2">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-white">
                    <tr className="text-left text-gray-500 border-b border-gray-200">
                      <th className="py-2 pr-2 w-8">
                        <input
                          type="checkbox"
                          checked={allIncluded}
                          onChange={toggleAllIncluded}
                        />
                      </th>
                      <th className="py-2 pr-2 w-6" />
                      <th className="py-2 pr-3">Company</th>
                      <th className="py-2 pr-3">MGT-7</th>
                      <th className="py-2 pr-3">PAS-3</th>
                      <th className="py-2 pr-3">AOC-4</th>
                      <th className="py-2 pr-3">Notes</th>
                      <th className="py-2 pr-3">Captable</th>
                      <th className="py-2">Financial</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => {
                      const sel = r.selection;
                      const isExpanded = expanded.has(r.cin);
                      const capResult = captableResults[r.cin];
                      const finResult = financialResults[r.cin];
                      return (
                        <Fragment key={r.cin}>
                          <tr className="border-b border-gray-100 align-top">
                            <td className="py-2 pr-2">
                              <input
                                type="checkbox"
                                checked={included(r)}
                                onChange={() => toggleExcluded(r.cin)}
                              />
                            </td>
                            <td className="py-2 pr-2">
                              {sel && (
                                <button
                                  onClick={() => toggleExpanded(r.cin)}
                                  className="text-gray-400 hover:text-gray-600"
                                  title="Show picked files"
                                >
                                  {isExpanded ? (
                                    <ChevronDown className="w-3.5 h-3.5" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5" />
                                  )}
                                </button>
                              )}
                            </td>
                            <td className="py-2 pr-3">
                              <div
                                className={
                                  r.name
                                    ? "text-gray-800"
                                    : "text-gray-400 italic"
                                }
                              >
                                {r.name || "Unknown"}
                              </div>
                              <div className="font-mono text-gray-400">
                                {r.cin}
                              </div>
                            </td>
                            {r.loading ? (
                              <td colSpan={4} className="py-2 pr-3">
                                <span className="inline-flex items-center gap-1 text-gray-400">
                                  <Loader2 className="w-3 h-3 animate-spin" />
                                  Loading files…
                                </span>
                              </td>
                            ) : r.loadError ? (
                              <td
                                colSpan={4}
                                className="py-2 pr-3 text-red-500"
                              >
                                {r.loadError}
                              </td>
                            ) : (
                              <>
                                <td className="py-2 pr-3">
                                  {sel?.mgt7 ? (
                                    <span
                                      className="text-gray-700"
                                      title={sel.mgt7.path}
                                    >
                                      {sel.mgt7.date ?? baseName(sel.mgt7.path)}
                                    </span>
                                  ) : (
                                    <span className="text-red-500">None</span>
                                  )}
                                </td>
                                <td className="py-2 pr-3">
                                  {sel && sel.pas3.length > 0 ? (
                                    <span className="text-gray-700">
                                      {sel.pas3.length}
                                    </span>
                                  ) : (
                                    <span className="text-red-500">0</span>
                                  )}
                                </td>
                                <td className="py-2 pr-3">
                                  {sel && sel.aoc4.length > 0 ? (
                                    <span className="text-gray-700">
                                      {sel.aoc4.length}
                                    </span>
                                  ) : (
                                    <span className="text-red-500">0</span>
                                  )}
                                </td>
                                <td className="py-2 pr-3">
                                  {sel?.warnings.length ? (
                                    <span
                                      className="text-amber-600"
                                      title={sel.warnings.join("\n")}
                                    >
                                      {sel.warnings.join("; ")}
                                    </span>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </td>
                              </>
                            )}
                            <td className="py-2 pr-3">
                              {!r.loading &&
                              !r.loadError &&
                              !r.captableReady ? (
                                <span
                                  className="text-gray-400 italic"
                                  title="Needs an MGT-7 and at least one PAS-3"
                                >
                                  Not eligible
                                </span>
                              ) : (
                                <ResultCell
                                  result={capResult}
                                  liveStatus={liveStatus(
                                    capResult,
                                    captableRuns,
                                  )}
                                />
                              )}
                            </td>
                            <td className="py-2">
                              {!r.loading &&
                              !r.loadError &&
                              !r.financialReady ? (
                                <span
                                  className="text-gray-400 italic"
                                  title="Needs at least one AOC-4"
                                >
                                  Not eligible
                                </span>
                              ) : (
                                <ResultCell
                                  result={finResult}
                                  liveStatus={liveStatus(
                                    finResult,
                                    financialRuns,
                                  )}
                                />
                              )}
                            </td>
                          </tr>
                          {isExpanded && sel && (
                            <tr className="border-b border-gray-100 bg-gray-50">
                              <td />
                              <td />
                              <td colSpan={7} className="py-2 pr-3">
                                <div className="space-y-1">
                                  {sel.mgt7 && (
                                    <div>
                                      <span className="font-medium text-gray-600">
                                        MGT-7:{" "}
                                      </span>
                                      <span className="text-gray-500">
                                        {baseName(sel.mgt7.path)}
                                        {sel.mgt7.date
                                          ? ` (${sel.mgt7.date})`
                                          : ""}
                                      </span>
                                    </div>
                                  )}
                                  {sel.pas3.length > 0 && (
                                    <div>
                                      <span className="font-medium text-gray-600">
                                        PAS-3:
                                      </span>
                                      <ul className="ml-4 list-disc text-gray-500">
                                        {sel.pas3.map((f) => (
                                          <li key={f.path} title={f.path}>
                                            {baseName(f.path)}
                                            {f.date ? ` (${f.date})` : ""}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                  {sel.aoc4.length > 0 && (
                                    <div>
                                      <span className="font-medium text-gray-600">
                                        AOC-4:
                                      </span>
                                      <ul className="ml-4 list-disc text-gray-500">
                                        {sel.aoc4.map((f) => (
                                          <li key={f.path} title={f.path}>
                                            {baseName(f.path)}
                                            {f.date ? ` (${f.date})` : ""}
                                          </li>
                                        ))}
                                      </ul>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between gap-3 px-4 py-3 border-t border-gray-200 flex-shrink-0">
                <span className="text-xs text-gray-500">
                  {anyLoading
                    ? "Loading company files…"
                    : `${captablePending.length} ready for captable · ${financialPending.length} ready for financial`}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleExtractCaptables}
                    disabled={
                      !!submitting || anyLoading || captablePending.length === 0
                    }
                    className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-violet-600 hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                  >
                    {submitting === "captable" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Extract Captables ({captablePending.length})
                  </button>
                  <button
                    onClick={handleExtractFinancials}
                    disabled={
                      !!submitting ||
                      anyLoading ||
                      financialPending.length === 0
                    }
                    className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed rounded"
                  >
                    {submitting === "financial" ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                    Extract Financials ({financialPending.length})
                  </button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
